import React, { useState } from "react";
import "./Header.css";
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import IconNotification from "@mui/icons-material/NotificationsOutlined";
import IconHelp from "@mui/icons-material/HeadsetMicOutlined";
import IconMenu from "@mui/icons-material/MenuRounded";
import { Badge, Tooltip, IconButton } from "@mui/material";
import UserDisplay from "./user/UserDisplay";
import SupportMenu from "./support/SupportMenu";
import NotificationsMenu from "./notifications/NotificationsMenu";
import NotificationToast from "./notifications/NotificationToast";
import useNotifications from "./notifications/useNotifications";
import useTopBarsHeight from "./useTopBarsHeight";
import AppConfig from '../consts/AppConfig';
import { DataProviderInstance } from "../api/DataProvider";
import { getRoleDisplayName } from "../utils/RoleManager";
import RemotePaths from "../consts/RemotePaths";
import TrialBanner from '../components/trial-banner/TrialBanner';
import DemoDataBanner from '../components/demo-data/DemoDataBanner';
import PastDueBanner from '../components/past-due-banner/PastDueBanner';
import CompanyClosureBanner from '../components/company-closure/CompanyClosureBanner';
import TermsReacceptanceDialog from '../components/termsReacceptance/TermsReacceptanceDialog';
import TutorialHelpButton from '../components/tutorial-help/TutorialHelpButton';
import useTutorialChapters, { lessonsForPath } from '../pages/system/tutorials/useTutorialChapters';

function Header(props) {
  const { isMobile, OpenMenuCallback } = props;
  const { UserData, CompanyConfig, session } = DataProviderInstance();
  // Sino: um poll a cada 30 s para o app inteiro (ver useNotifications)
  const notifications = useNotifications();
  // Faixas + cabeçalho grudam no topo no desktop (Header.css); a altura vai para --app-top-bars-height
  const topBarsRef = useTopBarsHeight();
  const [supportAnchorEl, setSupportAnchorEl] = useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const supportMenuOpen = Boolean(supportAnchorEl);
  const notificationsMenuOpen = Boolean(notificationsAnchorEl);
  
  function getHeaderText(pathname) {
    // Check if the last part of the path is a number (id)
    if (pathname.includes('/')) {
      const parts = pathname.split('/');
      const lastPart = parts[parts.length - 1];
      const isId = !isNaN(lastPart);
      if (isId) {
        pathname = pathname.replace(`/${lastPart}`, '/:id');
      }
    }

    let route = AppConfig.GetRouteLabel(pathname);
    return route ? route.label : "";
  }

  const location = useLocation();
  const pageTitle = getHeaderText(location.pathname);
  const { chapters: tutorialChapters } = useTutorialChapters();
  const screenLessons = lessonsForPath(tutorialChapters, location.pathname);
  const breadcrumbItems = [CompanyConfig.Name, pageTitle].filter(Boolean);


  function renderBreadcrumb() {
    return (
      <div className="header-breadcrumb">
        {breadcrumbItems.map((item, index) => (
          <React.Fragment key={index}>
            <h4>{item}</h4>
            {index < breadcrumbItems.length - 1 && (
              <FontAwesomeIcon icon={faChevronRight} className="header-breadcrumb-icon" />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }

  const handleSupportClick = (event) => {
    setSupportAnchorEl(event.currentTarget);
  };

  const handleSupportClose = () => {
    setSupportAnchorEl(null);
  };

  const handleNotificationsClick = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
    notifications.refresh();
  };
  
  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  return (
    <>
      <TermsReacceptanceDialog />
      <div className="header-sticky" ref={topBarsRef}>
      <TrialBanner />
      <DemoDataBanner />
      <PastDueBanner />
      <CompanyClosureBanner />
      <header className="header">
      {isMobile && (
        <IconButton
          onClick={OpenMenuCallback}
          size="small"
          sx={{
            color: 'var(--color-text-medium)',
            '&:hover': { backgroundColor: 'var(--color-blue-light4)' }
          }}
        >
          <IconMenu />
        </IconButton>
      )}
      
      {!isMobile && renderBreadcrumb()}
      
      <div className="header-actions">
        <TutorialHelpButton key={location.pathname} lessons={screenLessons} />

        <Tooltip title="Suporte" arrow>
          <IconButton
            size="small"
            onClick={handleSupportClick}
            sx={{
              color: 'var(--color-text-medium)',
              '&:hover': { backgroundColor: 'var(--color-blue-light4)' }
            }}
          >
            <IconHelp />
          </IconButton>
        </Tooltip>
        <SupportMenu
          anchorEl={supportAnchorEl}
          open={supportMenuOpen}
          onClose={handleSupportClose}
        />
        
        <Tooltip title={`Notificações`} arrow>
          <IconButton
            size="small"
            onClick={handleNotificationsClick}
            sx={{
              color: 'var(--color-text-medium)',
              '&:hover': { backgroundColor: 'var(--color-blue-light4)' }
            }}
          >
            <Badge
              badgeContent={notifications.unreadCount}
              max={99}
              color="error"
              sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem' } }}
            >
              <IconNotification />
            </Badge>
          </IconButton>
        </Tooltip>
        <NotificationsMenu
          anchorEl={notificationsAnchorEl}
          open={notificationsMenuOpen}
          onClose={handleNotificationsClose}
          items={notifications.items}
          unreadCount={notifications.unreadCount}
          loaded={notifications.loaded}
          onMarkRead={notifications.markRead}
          onMarkAllRead={notifications.markAllRead}
        />
        <NotificationToast
          key={notifications.newItem ? `${notifications.newItem.id}-${notifications.newItem.updated_at}` : 'none'}
          item={notifications.newItem}
          onClose={notifications.dismissNewItem}
          onMarkRead={notifications.markRead}
        />
        
        <UserDisplay
          Picture={UserData.profile.picture ? RemotePaths.PATH_USER_UPLOADS_USER_PICTURE(UserData.id) + "/" + UserData.profile.picture : null}
          Name={UserData.profile.name}
          Role={getRoleDisplayName(session?.companies?.find(company => company?.id == session?.currentCompany)?.role?.name || '')}
        />
      </div>
    </header>
      </div>
  </>
);
}

export default Header;